'use client';

/**
 * useComposerAttachments
 *
 * File/screenshot attachment state for the Meridian composer. Owns the hidden
 * pickers, the upload call, the uploaded-part list, and the in-place error, so
 * every composer surface (start screen and active chat) attaches files exactly
 * the same way instead of each host re-implementing it.
 *
 * Uploads are filed under `chatId`. The start screen mints its chat id on the
 * client BEFORE the chat row exists (see `generateClientChatId`) and creates the
 * row with that same id on send, so a file attached before the first message
 * lands under the chat it belongs to — no orphan chat rows, no re-upload.
 */

import { useCallback, useRef, useState } from 'react';
import type { FilePart } from '../../../lib/db/types';

/** Accept list for the "Add files" picker (documents + images). */
const FILE_ACCEPT = '.pdf,.txt,.csv,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx';
/** Accept list for the "Add screenshots" picker (images only). */
const IMAGE_ACCEPT = 'image/*';

export interface ComposerAttachments {
  uploadedFiles: FilePart[];
  isUploading: boolean;
  uploadError: string | null;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  screenshotInputRef: React.RefObject<HTMLInputElement | null>;
  fileAccept: string;
  imageAccept: string;
  openFilePicker: () => void;
  openScreenshotPicker: () => void;
  handleFileInputChange: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  removeFile: (fileId: string) => void;
  clearFiles: () => void;
}

export interface UseComposerAttachmentsParams {
  /** Chat the uploads are filed under (client-minted on the start screen). */
  chatId: string;
  /** Meridian API base; uploads POST to `${apiBaseUrl}/upload`. */
  apiBaseUrl?: string;
  /** Notified with each newly uploaded part (hosts that mirror the list). */
  onFilesSelected?: (files: FilePart[]) => void;
}

export function useComposerAttachments({
  chatId,
  apiBaseUrl = '/api/meridian',
  onFilesSelected,
}: UseComposerAttachmentsParams): ComposerAttachments {
  const [uploadedFiles, setUploadedFiles] = useState<FilePart[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const screenshotInputRef = useRef<HTMLInputElement>(null);

  const handleFileInputChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setIsUploading(true);
      setUploadError(null);

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('chatId', chatId);

        const response = await fetch(`${apiBaseUrl}/upload`, {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.error || 'Upload failed');
        }

        const { success, data: filePart, error } = await response.json();

        if (!success || !filePart) {
          throw new Error(error || 'Upload failed');
        }

        setUploadedFiles((prev) => [...prev, filePart]);
        onFilesSelected?.([filePart]);
      } catch (error) {
        // Surface the failure in place so the user knows the attach failed and
        // doesn't send assuming the file is attached (ERROR_STATES_ACTIONABLE).
        const message = error instanceof Error ? error.message : 'Upload failed';
        setUploadError(`Couldn't attach ${file.name}: ${message}`);
      } finally {
        setIsUploading(false);
        // Reset input so re-selecting the same file fires onChange again.
        event.target.value = '';
      }
    },
    [apiBaseUrl, chatId, onFilesSelected]
  );

  const openFilePicker = useCallback(() => fileInputRef.current?.click(), []);
  const openScreenshotPicker = useCallback(() => screenshotInputRef.current?.click(), []);

  const removeFile = useCallback((fileId: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.fileId !== fileId));
  }, []);

  const clearFiles = useCallback(() => {
    setUploadedFiles([]);
    setUploadError(null);
  }, []);

  return {
    uploadedFiles,
    isUploading,
    uploadError,
    fileInputRef,
    screenshotInputRef,
    fileAccept: FILE_ACCEPT,
    imageAccept: IMAGE_ACCEPT,
    openFilePicker,
    openScreenshotPicker,
    handleFileInputChange,
    removeFile,
    clearFiles,
  };
}
